<template>
  <!-- Mahmoud Elfarargy September, 4th, 2023 -->
  <div id="imageSlider" class="relative w-full">
    <!-- pop-up modal -->
    <client-only>
      <Transition name="product-image-viewer">
        <FullViewedImage
          v-if="showModal"
          :changeModalState="(val) => (showModal = val)"
          :allItems="allItems"
          :selectedItem="selectedItem"
          :seletedModalData="seletedModalData"
          :setSeletedModalData="setSeletedModalData"
        />
      </Transition>
    </client-only>

    <div
      v-if="allItems && allItems.length > 0"
      class="w-full flex flex-col justify-center max-h-[908px] lg:sticky lg:top-[80px]"
    >
    <!-- Product info bar -->
    <Transition name="slide-vertically" mode="in-out">
      <div v-if="isMounted && showBriefProductInfoBar" class="bg-black/50 backdrop-blur-md shadow-[0_4px_6px_rgba(0,0,0,0.1)] w-full px-4 hidden lg:flex lg:flex-nowrap justify-between items-center sm:absolute top-0 z-[2] rounded-[1.25rem_1.25rem_0_0] overflow-hidden">
        <!-- product name -->
        <h6 class="text-white text-[0.9rem] md:text-[1rem] lg:text-[1.1rem] xl:text-[1.19rem] 3xl:text-[1.2rem] font-bold ellipsis-x1">
          {{ productData[finalizedNameKey] }}
        </h6>
        <!-- product price -->
        <div>
          <div v-if="willPriceBeDeterminedLater"
            class="mt-5 py-3 gap-1.5 text-white text-[15px] md:text-[16.5px]">
            <span>{{ $t('general.price_will_be_specificed_later') }}</span>
          </div>
          <!-- actual price -->
          <section v-else class="flex flex-col flex-nowrap py-2 gap-0.5 text-white">
            <div v-if="updatedPrice > 0" class="flex flex-nowrap whitespace-nowrap gap-1">
                <span class="inline-flex font-bold text-[0.8rem]md:text-[1rem] lg:text-[1.05rem] xl:text-[1.09rem] 3xl:text-[1.1rem]">
                  {{ arabizeNumbers(priceAfterSelectingBranch).toLocaleString() }}
                </span>
                <span class="text-[1rem] md:text-[1.063rem] xl:text-[1.068rem]">{{ $t('general.egyptian_pound') }}</span>
            </div>
            <strong v-if="updatedPrice == 0">{{ $t('general.free') }}</strong>
          </section>
        </div>

      </div>
    </Transition>

      <!-- main image viewer -->
    <swiper
        v-if="allowSwiperToRender"
        :style="{
          '--swiper-navigation-color': '#fff',
          '--swiper-pagination-color': '#fff',
        }"
        :initialSlide="currentImageIndex"
        @swiper="onSwiper"
        :lazy="true"
        :pagination="{
          clickable: true,
        }"
        :dir="isRightToLeft ? 'rtl' : 'ltr'"
        :navigation="true"
        :modules="modules"
        @active-index-change="onItemSelectionChange"
        class="mySwiper rounded-md md:rounded-[1.25rem]"
    >
    
    <SwiperSlide v-for="(item, index) in allItems" :key="index">
      <button
        aria-label="open image"
        :class="
          `single_product_viewer w-full !outline-none ${
            !hasFailedToLoadMainImage ? 'cursor-zoom-in' : ''
          }`.trim()
        "
        @click="(event) => viewProductZoomedIn(item, event)"
      >
        <img
          :alt="imageAltText"
          :src="item.image || defaultStoreImage"
          @error="
            onImageError($event);
            hasFailedToLoadMainImage = true;
          "
          @loadedmetadata="hasFailedToLoadMainImage = false"
          draggable="false"
          class="w-full max-w-auto bg-black xl:max-w-none h-[255px] sm:h-[350px] lg:h-[550px] object-contain select-none max-h-[690px] md:max-h-[60vh] md:min-w-[150px] mx-auto"
          width="433"
          height="550"
        />
      </button>
    </SwiperSlide>

  </swiper>
      <client-only>
        <!-- images selector -->
        <ItemsSlider
          class="mt-7 sm:mt-6 md:mt-10"
          gap="2"
          ref="imagesList"
          wrapperTagType="ul"
        >
          <li
            v-for="(item, index) in allItems"
            @keydown="(event) => changeSelectedImage(event, index)"
            @dblclick="viewProductZoomedIn(item)"
            :tabIndex="0"
            class="m-0 p-0 select-none w-[4.688rem] h-[4.688rem] md:w-[6.063rem] md:h-[6.063rem] lg:w-[6.20rem] lg:h-[6.20rem] xl:w-[6.25rem] xl:h-[6.25rem] 3xl:w-[6.563rem] 3xl:h-[6.563rem]"
            :key="index"
            :id="item && item.id"
          >
            <!-- Facebook item -->
            <button
              v-if="item.id === 'facebook'"
              aria-label="view product video"
              :class="`relative bg-transparent w-full h-full cursor-pointer products-images rounded-[0.625rem] border-[3px] ${
                selectedItem.id === item.id ? 'selected-item' : ''
              }`"
              @click="() => viewProductZoomedIn(item)"
            >
              <img
                :src="item.image"
                @error="onImageError"
                :draggable="false"
                :alt="`${imageAltText} - Facebook video`"
                class="w-full h-full cursor-pointer products-images aspect-square rounded-[0.625rem] object-fill"
              />
              <img
                class="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[35px] h-[35px] md:w-[40px] md:h-[40px]"
                :src="playBtnIcon"
                alt="Play button"
                width="40"
                height="40"
              />
            </button>
            <!-- Youtube item -->
            <button
              v-if="item.id === 'youtube'"
              aria-label="view product video"
              :class="`relative bg-transparent w-full h-full cursor-pointer rounded-[0.625rem] products-images border-[3px] ${
                selectedItem.id === item.id ? 'selected-item' : ''
              }`"
              @click="() => viewProductZoomedIn(item)"
            >
              <img
                :src="item.image || getYouTubeThumbnail"
                @error="onImageError"
                :draggable="false"
                :alt="`${imageAltText} - YouTube video`"
                class="w-full h-full cursor-pointer products-images aspect-square rounded-[0.625rem] object-fill"
              />
              <img
                class="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[35px] h-[35px] md:w-[40px] md:h-[40px]"
                :src="playBtnIcon"
                alt="Play button"
                width="40"
                height="40"
              />
            </button>
            <!-- Image items -->
            <button v-else aria-label="view product"  class="!outline-none w-full h-full">
              <img
                :src="item.image || defaultStoreImage"
                :draggable="false"
                @error="onImageError"
                :alt="imageAltText"
                :class="`w-full h-full cursor-pointer products-images aspect-square border-[3px] rounded-[0.625rem] object-contain ${
                  selectedItem.id === item.id ? 'selected-item' : ''
                }`"
                @click="(e) => onProductSelect(e, item, index)"
                width="105"
                height="105"
              />
            </button>
          </li>
        </ItemsSlider>
      </client-only>
    </div>
    <div v-else>{{ $t("user.no_images_available") }}</div>
  </div>
</template>

<script>
import defaultStoreImage from "@/assets/images/default-user.svg";
import placeholderImg from "@/assets/images/placeholder(doNotRemove).jpg";
import { extractYouTubeId, lowerString,  onImageError } from "@/helpers";
import playBtnIcon from "@/assets/icons/play.svg";
import { ItemsSlider } from "@/components";
import FullViewedImage from "./FullViewedImage.vue";
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination, Navigation } from 'swiper/modules';
import { nextTick, ref } from "vue";
// Icons
import Star from "@/assets/shared/icons/Star.vue";

// Assets

export default {
  name: "product-image-slider",
  setup() {
    const usedCookieLanguage = useCookie('localStorageCopy')?.value?.language || 'ar';
    const swiperInstance = ref(null);
    const allowSwiperToRender = ref(true);

    const onSwiper = (swiper) => {
      swiperInstance.value = swiper;
    };
    const goToSlide = (index) => {
      // update the Swiper's active index
      if (swiperInstance.value) {
        swiperInstance.value.slideTo(index);
      }
    };

    // Workaround: the reason why we force re-rendering Swiper.js is because the package doesn't provide a way
    // to update the layout direction (from rtl to ltr or vise versa) after the initial rendering.
    // So when we change the language, we force re-rendering the package to make this functionality work correctly.
    const forceRerenderSwiper = async () => {
      allowSwiperToRender.value = false;
      await nextTick();
      allowSwiperToRender.value = true;
    };

    return {
        modules: [Pagination, Navigation],
        usedCookieLanguage,
        allowSwiperToRender,
        onSwiper,
        goToSlide,
        forceRerenderSwiper
    };
  },
  data() {
    return {
      isMounted: false,
      selectedItem: {},
      seletedModalData: {},
      imagesArr: [],
      defaultStoreImage,
      isVideoReadyToBePlayed: false,
      showModal: false,
      hasFailedToLoadMainImage: false,
      placeholderImg,
      playBtnIcon,
      currentImageIndex: 0,
    };
  },
  props: {
    youtubeURL: {
      type: String,
      required: true,
      default: "",
    },
    productData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    facebookURL: {
      type: String,
      required: true,
      default: "",
    },
    showBriefProductInfoBar: {
      type: Boolean,
      required: true,
      default: false
    },
    willPriceBeDeterminedLater: {
      type: Boolean,
      default: false,
      required: true,
    },
    updatedPrice: {
      type: [Number, String],
      required: true,
      default: () => 0
    },
    priceAfterSelectingBranch: {
      type: [Number, NaN],
      required: true,
      default: NaN
    },
    getDiscountPercentage: {
      type: [Number, NaN],
      required: false,
      default: NaN
    }
  },
  components: {
    Star,
    ItemsSlider,
    FullViewedImage,
    Swiper,
    SwiperSlide,
  },
  computed: {
    allItems() {
      // merges the main image with other images
      return this.productData?.images?.length > 0
        ? [
            ...this.productData.images,
            ...(this.youtubeURL ? [
              {
                id: "youtube",
                image: this.getYouTubeThumbnail,
                url: this.youtubeURL,
              },
            ] : []),
            ...(this.facebookURL ? [
              {
                id: "facebook",
                image: this.productData?.image,
                url: this.facebookURL,
              },
            ] : []),
          ]
        : this.productData.image
        ? []
        : [];
    },
    getYouTubeThumbnail() {
      return this.youtubeURL
        ? `http://img.youtube.com/vi/${extractYouTubeId(this.youtubeURL)}/0.jpg`
        : "";
    },
    isRightToLeft() {
      return lowerString(this.currentlySelectedLanguage || this.usedCookieLanguage) == "ar";
    },
    finalizedNameKey() {
      return this.nameKey || `name_${this.usedCookieLanguage}`;
    },
    imageAltText() {
      // Use image_alt_text if available, otherwise fallback to product name
      if (this.productData?.image_alt_text) {
        return this.productData.image_alt_text;
      }
      // Fallback to product name
      return this.productData?.[this.finalizedNameKey] || this.productData?.name_ar || this.productData?.name_en || '';
    },
  },
  methods: {
    onImageError,
    changeSelectedImage(event) {
      const moveSelector = (direction) => {
        // disable if the modal is shown
        if (this.showModal || this.allItems?.length < 1) {
          return;
        }

        let imageIndexVar = this.currentImageIndex;
        // ltr -> go left | rtl -> go right
        if (direction == "left") {
          if (imageIndexVar < this.allItems?.length - 1) {
            imageIndexVar++;
          } else {
            imageIndexVar = 0;
          }
          // ltr -> go right | rtl -> go left
        } else if (direction == "right") {
          if (imageIndexVar > 0) {
            imageIndexVar--;
          } else {
            imageIndexVar = this.allItems.length - 1;
          }
        }
        if (typeof this.allItems?.[imageIndexVar] !== "undefined") {
          this.selectedItem = this.allItems[imageIndexVar];
        }

        this.currentImageIndex = imageIndexVar;
        this.goToSlide(imageIndexVar);
      };
      var keycode;
      if (event.keyCode) {
        keycode = event.keyCode;
      } else if (event.which) {
        keycode = event.which;
      }

      switch (keycode) {
        // right arrow
        case 39:
          moveSelector(this.isRightToLeft ? "right" : "left");
          break;
        // left arrow
        case 37:
          moveSelector(this.isRightToLeft ? "left" : "right");
          break;
        // enter button
        case 13:
          this.viewProductZoomedIn(this.selectedItem);
          break;
        default:
          () => {};
      }
    },
    onItemSelectionChange(e){
      const { activeIndex } = e || {};
      const activeItem = activeIndex <= this.allItems.length ? this.allItems[activeIndex] : null;
      if (activeItem) {
        this.currentImageIndex = activeIndex;
        this.selectedItem = activeItem;
        const imageElement = document.getElementById(activeItem.id);
        if (!this.showModal && imageElement) {
          imageElement.focus();
        }
      }
    },
    onProductSelect(event, clickedOnItem, index) {
      this.selectedItem = clickedOnItem;
      this.goToSlide(index);

      // keep the active element in the center if it has enough peripheral space around it
      const container = event.currentTarget.closest('.slider');
      const item = event.currentTarget;
      const itemLeft = item.offsetLeft;
      const itemWidth = item.offsetWidth;
      const targetScrollLeft = itemLeft - (container.offsetWidth / 2 - itemWidth / 2);
      container.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    },
    viewProductZoomedIn(item, event) {
      if(event){
        var nonClosableElements = [document.querySelector('.swiper-button-next'), document.querySelector('.swiper-button-prev'), document.querySelector('.swiper-pagination')];
        this.$nextTick(() => {
          // trigger clicking ouside these specific elements
          const allowModalClosing = nonClosableElements.some(element => element?.contains(event.target));
          if (allowModalClosing) {
            return;
          }
        })
      }
      if (this.hasFailedToLoadMainImage) {
        return;
      }

      if (item.id === "facebook") {
        window.open(item.url, "_blank");
      } else {
        this.showModal = !this.showModal;
        this.seletedModalData = item;
      }

      const imageIndex = this.allItems?.map((el) => el?.id)?.indexOf(item?.id);
      if (imageIndex >= 0) {
        this.currentImageIndex = imageIndex;
        this.goToSlide(imageIndex);
      }
      const imageElement = document.getElementById(item.id);
      if (imageElement) {
        imageElement.focus();
      }
    },
    setSeletedModalData(item, index) {
      this.seletedModalData = item;
      this.selectedItem = item;
      this.goToSlide(index);
    },
  },
  watch: {
    showModal(val) {
      if (typeof val === "boolean") {
        if (val) {
          document.body.style.overflowY = "hidden";
        } else {
          this.seletedModalData = false;
          document.body.style.overflowY = "auto";
        }
      }
    },
    isRightToLeft(){
      this.forceRerenderSwiper();
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.isMounted = true;
      this.selectedItem =
        this.productData?.images?.length > 0
          ? this.productData.images[0]
          : { id: "", image: "" };
    });
  },
};
</script>

<style lang="scss" scoped >
#imageSlider{
  .swiper, .swiper-wrapper{
    width: 100%;
  }
  .swiper-button-prev, .swiper-button-next{
    width: 3.8rem;
    height:3.8rem;
    background: rgba(0,0,0,.1);
    border-radius: 50%;
    &::after{
      font-size: 1.6rem;
    }
    @media only screen and (max-width: 670px){
      width: 2.8rem;
      height: 2.8rem;
      &::after{
        font-size: 1.4rem;
      }
    }
  }
}

.slide-vertically-enter-active{
  animation: slideup 0.1s ease-in-out;
}
.slide-vertically-leave-active{
  animation: slidedown 0.1s ease-in-out;
}
@keyframes slideup {
  from {
    transform: translateY(-10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
@keyframes slidedown {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(-10px);
      opacity: 0;
    }
}
.single_product_viewer {
  flex: 1;
  display: grid;
  place-items: center;
}

.vh--image.vh--abs {
  max-width: unset;
}
.products-images:not(.selected-item) {
  filter: brightness(0.8);
}
.selected-item {
  transition: 0.2s linear;
  transition-property: border-color, filter;
  border-color: var(--primary-lighter-clr);
  filter: brightness(1);
}
</style>
